import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useUser } from "@clerk/clerk-react";
import { toast } from "sonner";
import { storageService } from "@/lib/api/services/storage.service";
import { courseService } from "@/lib/api/services/course.service";
import type { Database } from "@/lib/supabase/supabase-types";

type Course = Database["public"]["Tables"]["courses"]["Row"];

interface UseCourseThumbnailOptions {
  courseId?: string;
  onSuccess?: (url: string) => void;
}

export function useCourseThumbnail({ courseId, onSuccess }: UseCourseThumbnailOptions = {}) {
  const { user } = useUser();
  const queryClient = useQueryClient();

  const uploadThumbnail = useMutation({
    mutationFn: async (file: File) => {
      if (!user?.id) throw new Error("No user ID");

      // Upload file to backend storage API
      const publicUrl = await storageService.uploadFile('course-thumbnails', file);

      // If courseId exists, update the course via backend API
      if (courseId) {
        const courseData = await courseService.updateCourse(courseId, { thumbnail_url: publicUrl });

        return { 
          courseData,
          publicUrl 
        };
      } else {
        // No course exists yet - just return the URL for later use
        return { 
          courseData: null,
          publicUrl 
        };
      }
    },
    onMutate: async (file: File) => {
      if (!courseId) return { previousCourse: null, optimisticUrl: null };
      
      const queryKey = ["course", courseId];
      
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey });

      // Snapshot the previous course
      const previousCourse = queryClient.getQueryData<Course>(queryKey);

      // Create optimistic preview URL for the uploaded file
      const optimisticUrl = URL.createObjectURL(file);

      // Optimistically update to show the new image
      if (previousCourse) {
        const optimisticData = {
          ...previousCourse,
          thumbnail_url: optimisticUrl,
        };
        queryClient.setQueryData(queryKey, optimisticData);
      }

      return { previousCourse, optimisticUrl };
    },
    onError: (err, _file, context) => {
      if (!courseId) return;
      
      const queryKey = ["course", courseId];
      
      // If the mutation fails, roll back the course cache
      if (context?.previousCourse) {
        queryClient.setQueryData(queryKey, context.previousCourse);
      }
      // Clean up the optimistic URL
      if (context?.optimisticUrl) {
        URL.revokeObjectURL(context.optimisticUrl);
      }
      console.error('Course thumbnail upload failed:', err);
      toast.error('Failed to upload thumbnail. Please try again.');
    },
    onSuccess: (result, _file, context) => {
      // Clean up optimistic URL if exists
      if (context?.optimisticUrl) {
        // Preload the new image before cleaning up the blob URL
        const img = new Image();
        img.onload = () => {
          URL.revokeObjectURL(context.optimisticUrl);
        };
        img.onerror = () => {
          URL.revokeObjectURL(context.optimisticUrl);
        };
        img.src = result.publicUrl;
      }
      
      // Update cache with actual data if course was updated
      if (courseId && result.courseData) {
        const queryKey = ["course", courseId];
        queryClient.setQueryData(queryKey, result.courseData);
        // Also invalidate courses list
        queryClient.invalidateQueries({ queryKey: ["courses"] });
      }
      
      // Call success callback if provided
      if (onSuccess) {
        onSuccess(result.publicUrl);
      }
      
      toast.success('Thumbnail uploaded successfully!');
    },
  });

  const deleteThumbnail = useMutation({
    mutationFn: async (thumbnailUrl: string) => {
      if (!user?.id) throw new Error("No user ID");

      // Try to extract the file path from the URL
      // Supabase storage URLs have format: https://...supabase.co/storage/v1/object/public/bucket/path
      const urlParts = thumbnailUrl.split('/storage/v1/object/public/course-thumbnails/');
      let wasStorageFile = false;

      if (urlParts.length === 2) {
        // It's a storage URL, try to delete from storage
        const filePath = urlParts[1];
        try {
          await storageService.deleteFile('course-thumbnails', filePath);
          wasStorageFile = true;
        } catch (error) {
          // If deletion fails, just clear the URL from the course
          console.warn('Failed to delete file from storage, clearing URL only:', error);
        }
      }

      // Update the course to remove the thumbnail URL via backend API
      if (courseId) {
        const courseData = await courseService.updateCourse(courseId, { thumbnail_url: null });
        return { courseData, wasStorageFile };
      }

      return { courseData: null, wasStorageFile };
    },
    onMutate: async (_thumbnailUrl: string) => {
      if (!courseId) return { previousCourse: null };
      
      const queryKey = ["course", courseId];
      
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey });

      // Snapshot the previous course
      const previousCourse = queryClient.getQueryData<Course>(queryKey);

      // Optimistically update to remove the image
      if (previousCourse) {
        const optimisticData = {
          ...previousCourse,
          thumbnail_url: null,
        };
        queryClient.setQueryData(queryKey, optimisticData);
      }

      return { previousCourse };
    },
    onError: (err, _thumbnailUrl, context) => {
      if (!courseId) return;
      
      const queryKey = ["course", courseId];
      
      // If the mutation fails, roll back the course cache
      if (context?.previousCourse) {
        queryClient.setQueryData(queryKey, context.previousCourse);
      }
      console.error('Course thumbnail deletion failed:', err);
      toast.error('Failed to remove thumbnail. Please try again.');
    },
    onSuccess: (result) => {
      if (!courseId) return;

      const queryKey = ["course", courseId];
      
      // Update cache with the actual server data
      if (result.courseData) {
        queryClient.setQueryData(queryKey, result.courseData);
        // Also invalidate courses list
        queryClient.invalidateQueries({ queryKey: ["courses"] });
      }
      
      toast.success(result.wasStorageFile ? 'Thumbnail removed successfully!' : 'Thumbnail URL cleared!');
    },
  });

  const isStorageUrl = (url: string | null | undefined): boolean => {
    if (!url) return false;
    return url.includes('/storage/v1/object/public/course-thumbnails/');
  };

  return {
    uploadThumbnail: uploadThumbnail.mutate,
    deleteThumbnail: deleteThumbnail.mutate,
    isUploading: uploadThumbnail.isPending,
    isDeleting: deleteThumbnail.isPending,
    uploadedImageUrl: uploadThumbnail.data?.publicUrl || null,
    isUploadSuccess: uploadThumbnail.isSuccess,
    isStorageUrl,
  };
}